/**
 * Rich Text Widget (Story 5.24, Task 4)
 *
 * Renders formatted text with visual variants:
 * - body: standard body text
 * - callout: highlighted background with left accent border
 * - quote: italic with left border, indented
 *
 * Supports auto-sizing via useAutoResize hook.
 * Content is plain text only — no HTML rendering (XSS prevention).
 */

import type { WidgetConfig } from "../../../lib/templateUtils";
import { useAutoResize } from "../../../lib/useAutoResize";

interface RichTextWidgetProps {
  widget: WidgetConfig;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
}

type RichTextVariant = "body" | "callout" | "quote";

/** Row span at which content becomes scrollable to prevent excessive widget height */
const OVERFLOW_ROW_THRESHOLD = 8;

const VARIANT_CLASSES: Record<RichTextVariant, string> = {
  body: "text-[13.5px] leading-relaxed text-foreground",
  callout: "text-[13.5px] leading-relaxed text-foreground bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-400 p-4 rounded-r-md",
  quote: "text-[13.5px] leading-relaxed italic text-muted-foreground border-l-2 border-border pl-4",
};

export function RichTextWidget({ widget, onAutoResize }: RichTextWidgetProps) {
  const config = widget.config as { content?: string; variant?: RichTextVariant };
  const content = config.content ?? "";
  const variant = config.variant ?? "body";
  const currentRowSpan = widget.position.rowSpan ?? 1;
  const isAutoSized = widget.autoSize !== false;

  const containerRef = useAutoResize({
    currentRowSpan,
    enabled: isAutoSized && !!content && !!onAutoResize,
    onAutoResize: (newRowSpan) => onAutoResize?.(widget.id, newRowSpan),
  });

  if (!content) {
    return (
      <div data-testid="widget-type-rich-text" className="flex h-full flex-col p-4">
        <h3 className="mb-2 truncate text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
          {widget.title}
        </h3>
        <div className="flex flex-1 items-center justify-center">
          <span className="text-[13.5px] italic text-muted-foreground" data-testid="rich-text-empty">
            Add content...
          </span>
        </div>
      </div>
    );
  }

  const overflowClass = currentRowSpan >= OVERFLOW_ROW_THRESHOLD ? "overflow-y-auto" : "";

  return (
    <div data-testid="widget-type-rich-text" className="flex h-full flex-col p-4">
      <h3 className="mb-2 truncate text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
        {widget.title}
      </h3>
      <div ref={containerRef} className={`min-w-0 flex-1 ${overflowClass}`}>
        <div className={VARIANT_CLASSES[variant]} data-testid="rich-text-content">
          {content}
        </div>
      </div>
    </div>
  );
}
